import { apiFiles } from "@/api/file.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import {
  NON_PREVIEWABLE_DOCUMENT_EXTENSIONS,
  useDocumentPreviewState,
  type UseDocumentPreviewStateParams,
} from "@/pages/employees/employee/widgets/use-document-preview-state.hook.ts";
import { DocumentPreviewStatus } from "@skailer-com/uikit";
import { useCallback, useEffect, useRef, useState } from "react";

const NON_PREVIEWABLE_IMAGE_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);
const NON_PREVIEWABLE_IMAGE_EXTENSIONS = new Set(["heic", "heif", "heics", "heifs"]);

export function usePrivateFilePreview({
  file,
  preview,
  refetchPreview,
  onDownloaded,
}: UseDocumentPreviewStateParams & { onDownloaded?: () => void }) {
  const { handleErrors } = useErrorHandle();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const previewObjectUrlRef = useRef<string | null>(null);
  const handleErrorsRef = useRef(handleErrors);

  const { sourceFile, hasPreviewContract, isPreparing, isReady, isPreviewFailed } =
    useDocumentPreviewState({ file, preview, refetchPreview });

  const mimeType = sourceFile?.mime_type.toLowerCase() ?? "";
  const extension = sourceFile?.name.split(".").pop()?.toLowerCase() ?? "";
  const isPdf = mimeType === "application/pdf";
  const isImageCandidate = mimeType.startsWith("image/");
  const isExplicitlyNonPreviewableDocument = NON_PREVIEWABLE_DOCUMENT_EXTENSIONS.has(extension);
  const isUnsupportedImage =
    NON_PREVIEWABLE_IMAGE_MIME_TYPES.has(mimeType) ||
    NON_PREVIEWABLE_IMAGE_EXTENSIONS.has(extension);
  const isImage =
    !!sourceFile && !isExplicitlyNonPreviewableDocument && isImageCandidate && !isUnsupportedImage;
  const isPdfPreviewable = !!sourceFile && !isExplicitlyNonPreviewableDocument && isPdf;
  const canRenderSource = isPdfPreviewable || isImage;
  const isPreviewable = hasPreviewContract ? isReady && canRenderSource : canRenderSource;

  const status: DocumentPreviewStatus = isPreviewFailed
    ? "failed"
    : isPreparing
      ? "pending"
      : isPreviewable
        ? "ready"
        : "unsupported";

  useEffect(() => {
    handleErrorsRef.current = handleErrors;
  }, [handleErrors]);

  const setPreviewObjectUrl = useCallback((nextUrl: string | null) => {
    if (previewObjectUrlRef.current && previewObjectUrlRef.current !== nextUrl) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
    }

    previewObjectUrlRef.current = nextUrl;
    setPreviewUrl(nextUrl);
  }, []);

  const triggerBrowserDownload = useCallback(
    (url: string) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.target = "_blank";
      link.rel = "noopener";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [file.name]
  );

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);

    try {
      const { data, error, headers } = await apiFiles.getPrivate(file.uuid);

      if (data) {
        const contentType = headers?.["content-type"];
        const blobType = typeof contentType === "string" ? contentType : file.mime_type;
        const blob = data instanceof Blob ? data : new Blob([data], { type: blobType });
        const downloadUrl = URL.createObjectURL(blob);

        onDownloaded?.();

        triggerBrowserDownload(downloadUrl);
        window.setTimeout(() => {
          URL.revokeObjectURL(downloadUrl);
        }, 60_000);
        return;
      }

      handleErrorsRef.current(error);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownloaded, file.mime_type, file.uuid, triggerBrowserDownload]);

  useEffect(() => {
    let isActive = true;

    const loadFileLink = async () => {
      setIsLoading(true);

      if (!isPreviewable || !sourceFile) {
        setPreviewObjectUrl(null);
        setIsLoading(false);
        return;
      }

      const { data, error, headers } = await apiFiles.getPrivate(sourceFile.uuid);

      if (!isActive) return;

      if (data) {
        const contentType = headers?.["content-type"];
        const blobType = typeof contentType === "string" ? contentType : sourceFile.mime_type;
        const blob = data instanceof Blob ? data : new Blob([data], { type: blobType });
        const objectUrl = URL.createObjectURL(blob);
        setPreviewObjectUrl(objectUrl);
      } else {
        setPreviewObjectUrl(null);
        handleErrorsRef.current(error);
      }

      setIsLoading(false);
    };

    void loadFileLink();

    return () => {
      isActive = false;
    };
  }, [sourceFile?.uuid, sourceFile?.mime_type, sourceFile, isPreviewable, setPreviewObjectUrl]);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, []);

  return { previewUrl, sourceFile, status, isLoading, isDownloading, handleDownload };
}
